import { LitElement, html, css, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { router } from '../../utils/router.js'
import { authService } from '../../services/auth/auth-service.js'
import { teamStorage, teamMemberStorage } from '../../storage/team-storage.js'
import { syncListOnNavigate } from '../../storage/sync-manager.js'
import {
  getNotifications,
  claimInvite,
  declineInvite,
  deleteNotification,
  sendTeamInvite,
  type Notification,
} from '../../services/teams/teams-api.js'
import type { Team } from '../../schemas/team.js'
import { showToast } from '../common/cairn-toast.js'
import type { User } from '../../schemas/user.js'
import { db } from '../../database/schema.js'

import type { JSONContent } from '../../lib/tiptap-config.js'
import type { RichTextContentEvent } from '../ui/rich-text-editor.js'
void import('../ui/rich-text-editor.js')

import '@material/web/button/filled-button.js'
import '@material/web/button/outlined-button.js'
import '@material/web/button/text-button.js'
import '@material/web/textfield/outlined-text-field.js'
import '@material/web/progress/linear-progress.js'

@customElement('cairn-teams')
export class CairnTeams extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
      background: var(--cairn-background-color, #f8fafc);
      overflow: hidden;
    }

    .header {
      background: var(--cairn-surface-color, #ffffff);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      padding: var(--cairn-spacing-md, 16px);
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      flex: 0 0 auto;
    }

    .back-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
    }

    .back-button:hover {
      background: rgba(59, 130, 246, 0.04);
      color: var(--cairn-primary-color, #3b82f6);
    }

    .title {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
    }

    @media (min-width: 769px) {
      .header {
        display: none;
      }
    }

    .sync-bar {
      --md-linear-progress-active-indicator-color: var(--cairn-primary-color, #3b82f6);
      --md-linear-progress-track-color: transparent;
      --md-linear-progress-track-height: 2px;
      --md-linear-progress-active-indicator-height: 2px;
      width: 100%;
      flex-shrink: 0;
    }

    .content {
      flex: 1;
      padding: var(--cairn-spacing-md, 16px);
      overflow-y: auto;
      min-height: 0;
    }

    .invite-banner {
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid #3b82f6;
      border-radius: var(--cairn-border-radius, 8px);
      padding: var(--cairn-spacing-md, 16px);
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .invite-banner-title {
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
      margin-bottom: var(--cairn-spacing-sm, 8px);
    }

    .invite-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: var(--cairn-spacing-sm, 8px) 0;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .invite-item:last-child {
      border-bottom: none;
    }

    .invite-info {
      flex: 1;
    }

    .invite-team-name {
      font-weight: 500;
      color: var(--cairn-text-color, #1e293b);
    }

    .invite-from {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .invite-actions {
      display: flex;
      gap: var(--cairn-spacing-sm, 8px);
    }

    .add-button {
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .add-button md-filled-button {
      width: 100%;
    }

    @media (min-width: 769px) {
      .add-button md-filled-button {
        width: auto;
      }
    }

    .create-form {
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      padding: var(--cairn-spacing-md, 16px);
      margin-bottom: var(--cairn-spacing-md, 16px);
      display: flex;
      flex-direction: column;
      gap: var(--cairn-spacing-sm, 8px);
    }

    .create-form md-outlined-text-field {
      width: 100%;
    }

    .form-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .form-actions {
      display: flex;
      gap: var(--cairn-spacing-sm, 8px);
      justify-content: flex-end;
    }

    .team-grid {
      display: grid;
      gap: 16px;
      grid-template-columns: 1fr;
    }

    @media (min-width: 769px) {
      .team-grid {
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      }
    }

    .team-card {
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      padding: var(--cairn-spacing-md, 16px);
      cursor: pointer;
      transition:
        box-shadow 0.2s,
        border-color 0.2s;
    }

    .team-card:hover {
      border-color: var(--cairn-primary-color, #3b82f6);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .team-name {
      font-size: var(--cairn-text-size-md, 16px);
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
      margin-bottom: 4px;
    }

    .team-description {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
      margin-bottom: 8px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .team-meta {
      display: flex;
      gap: var(--cairn-spacing-md, 16px);
      font-size: var(--cairn-text-size-xs, 12px);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .role-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: var(--cairn-text-size-xs, 12px);
      font-weight: 500;
      text-transform: uppercase;
    }

    .role-badge.owner {
      background: rgba(59, 130, 246, 0.1);
      color: #3b82f6;
    }

    .role-badge.member {
      background: rgba(100, 116, 139, 0.1);
      color: #64748b;
    }

    .empty-state {
      text-align: center;
      padding: var(--cairn-spacing-xl, 32px);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .empty-icon {
      font-size: 48px;
      margin-bottom: var(--cairn-spacing-md, 16px);
      font-family: 'Material Symbols Outlined';
    }

    .invite-section {
      position: relative;
    }

    .invite-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 8px;
    }

    .invite-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 8px 4px 12px;
      border-radius: 16px;
      font-size: var(--cairn-text-size-sm, 14px);
      background: rgba(59, 130, 246, 0.15);
      color: var(--cairn-text-color, #1e293b);
    }

    .invite-chip button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      border: none;
      background: none;
      border-radius: 50%;
      cursor: pointer;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: 16px;
      font-family: 'Material Symbols Outlined';
    }

    .invite-chip button:hover {
      background: rgba(0, 0, 0, 0.1);
      color: var(--cairn-text-color, #1e293b);
    }

    .suggestions {
      position: absolute;
      left: 0;
      right: 0;
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10;
      max-height: 200px;
      overflow-y: auto;
    }

    .suggestion-item {
      display: flex;
      flex-direction: column;
      padding: 10px 12px;
      cursor: pointer;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .suggestion-item:last-child {
      border-bottom: none;
    }

    .suggestion-item:hover {
      background: rgba(59, 130, 246, 0.04);
    }

    .suggestion-name {
      font-weight: 500;
      color: var(--cairn-text-color, #1e293b);
      font-size: var(--cairn-text-size-sm, 14px);
    }

    .suggestion-email {
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: var(--cairn-text-size-xs, 12px);
    }
  `

  @state() private teams: Array<Team & { role: string; memberCount: number }> = []
  @state() private notifications: Notification[] = []
  @state() private syncing = false
  @state() private showCreateForm = false
  @state() private createName = ''
  @state() private createDescription = ''
  @state() private createDescriptionRich: JSONContent | undefined = undefined
  @state() private creating = false
  @state() private inviteEmails: string[] = []
  @state() private inviteInput = ''
  @state() private knownUsers: User[] = []
  @state() private showSuggestions = false

  private authUnsubscribe?: () => void

  override connectedCallback(): void {
    super.connectedCallback()
    this.authUnsubscribe = authService.onChange(state => {
      if (state.status === 'authenticated') void this.loadData()
    })
    void this.loadData()
    void this.loadKnownUsers()
  }

  private async loadKnownUsers(): Promise<void> {
    const currentId = authService.getState().userId
    const allUsers = await db.users.toArray()
    this.knownUsers = allUsers.filter(u => u.id !== currentId)
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.authUnsubscribe?.()
  }

  private async loadData(): Promise<void> {
    await this.loadTeams()

    if (authService.getState().status !== 'authenticated') return

    this.syncing = true
    try {
      await syncListOnNavigate('teams')
      await this.loadTeams()
    } catch {
      // sync errors shown via toast
    }
    this.syncing = false

    await this.loadNotifications()
  }

  private async loadTeams(): Promise<void> {
    const userId = authService.getState().userId
    if (!userId) return

    const result = await teamStorage.getTeamsForUser(userId)
    if (!result.success || !result.data) {
      this.teams = []
      return
    }

    const teamsWithMeta = await Promise.all(
      result.data.map(async team => {
        const membership = await teamMemberStorage.getUserTeamMembership(team.id, userId)
        const members = await db.teamMembers.where('teamId').equals(team.id).count()
        return {
          ...team,
          role: membership.data?.role ?? 'member',
          memberCount: members,
        }
      })
    )

    this.teams = teamsWithMeta
  }

  private async loadNotifications(): Promise<void> {
    try {
      const all = await getNotifications()
      this.notifications = all.filter(
        n => (n.type === 'team-invite' || n.type === 'plan-invite') && !n.read
      )
    } catch {
      this.notifications = []
    }
  }

  private async handleCreateTeam(): Promise<void> {
    if (!this.createName.trim()) {
      showToast('error', 'Team name is required')
      return
    }

    const userId = authService.getState().userId
    if (!userId) {
      showToast('error', 'You must be signed in to create a team')
      return
    }

    this.creating = true
    try {
      const teamData: Parameters<typeof teamStorage.createTeam>[0] = {
        name: this.createName.trim(),
        description: this.createDescription.trim(),
        createdBy: userId,
      }
      if (this.createDescriptionRich) {
        teamData.descriptionRich = this.createDescriptionRich as {
          type: 'doc'
          content?: unknown[]
        }
      }
      const result = await teamStorage.createTeam(teamData)

      if (!result.success) {
        showToast('error', `Failed to create team: ${result.error?.message ?? 'Unknown error'}`)
        return
      }

      const teamId = result.data!.id
      await teamMemberStorage.inviteToTeam({
        teamId,
        userId,
        role: 'owner',
        invitedBy: userId,
      })

      for (const email of this.inviteEmails) {
        sendTeamInvite(teamId, email).catch(() => {})
      }

      this.showCreateForm = false
      this.createName = ''
      this.createDescription = ''
      this.createDescriptionRich = undefined
      this.inviteEmails = []
      this.inviteInput = ''

      await this.loadTeams()
      showToast('success', 'Team created')

      syncListOnNavigate('teams').catch(() => {})
    } catch (err) {
      showToast(
        'error',
        `Failed to create team: ${err instanceof Error ? err.message : String(err)}`
      )
    } finally {
      this.creating = false
    }
  }

  private async handleAcceptInvite(notification: Notification): Promise<void> {
    try {
      const result = await claimInvite(notification.inviteToken)
      if (result.success) {
        await deleteNotification(notification.id)
        this.notifications = this.notifications.filter(n => n.id !== notification.id)
        window.dispatchEvent(new Event('notifications-changed'))
        showToast(
          'success',
          `Joined ${result.entityName ?? result.teamName ?? result.planName ?? 'team'}`
        )
        await this.loadData()
      } else {
        showToast('error', result.error ?? 'Failed to accept invite')
      }
    } catch (e) {
      showToast('error', `Accept failed: ${e instanceof Error ? e.message : 'unknown error'}`)
    }
  }

  private async handleDeclineNotification(notification: Notification): Promise<void> {
    try {
      await declineInvite(notification.inviteToken)
      await deleteNotification(notification.id)
      this.notifications = this.notifications.filter(n => n.id !== notification.id)
      window.dispatchEvent(new Event('notifications-changed'))
      showToast('info', 'Invite declined')
    } catch (e) {
      showToast('error', `Decline failed: ${e instanceof Error ? e.message : 'unknown error'}`)
    }
  }

  private addInviteEmail(email: string): void {
    const trimmed = email.toLowerCase().trim()
    if (trimmed && trimmed.includes('@') && !this.inviteEmails.includes(trimmed)) {
      this.inviteEmails = [...this.inviteEmails, trimmed]
    }
    this.inviteInput = ''
    this.showSuggestions = false
  }

  private removeInviteEmail(email: string): void {
    this.inviteEmails = this.inviteEmails.filter(e => e !== email)
  }

  private handleInviteKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      if (this.inviteInput.trim()) {
        this.addInviteEmail(this.inviteInput)
      }
    }
    if (e.key === 'Backspace' && !this.inviteInput && this.inviteEmails.length > 0) {
      this.inviteEmails = this.inviteEmails.slice(0, -1)
    }
  }

  private handleInviteInput(e: InputEvent): void {
    const val = (e.target as HTMLInputElement).value
    if (val.includes(',')) {
      const parts = val.split(',')
      for (const part of parts.slice(0, -1)) {
        if (part.trim()) this.addInviteEmail(part)
      }
      this.inviteInput = parts[parts.length - 1]
    } else {
      this.inviteInput = val
    }
    this.showSuggestions = this.inviteInput.length > 0 && this.filteredSuggestions.length > 0
  }

  private get filteredSuggestions(): User[] {
    if (!this.inviteInput.trim()) return []
    const q = this.inviteInput.toLowerCase()
    return this.knownUsers
      .filter(u => {
        if (u.email && this.inviteEmails.includes(u.email.toLowerCase())) return false
        return u.displayName.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
      })
      .slice(0, 5)
  }

  private pullStartY = 0
  private pulling = false
  private scrollerEl: HTMLElement | null = null

  private handleTouchStart(e: TouchEvent): void {
    const scroller = e.currentTarget as HTMLElement
    this.scrollerEl = scroller
    if (scroller.scrollTop <= 0) {
      this.pullStartY = e.touches[0].clientY
      this.pulling = true
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.pulling || !this.scrollerEl) return
    const dy = e.touches[0].clientY - this.pullStartY
    if (dy > 0) {
      const clamped = Math.min(dy * 0.4, 80)
      this.scrollerEl.style.transform = `translateY(${clamped}px)`
    } else {
      this.pulling = false
      this.scrollerEl.style.transform = ''
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (!this.pulling) return
    const dy = e.changedTouches[0].clientY - this.pullStartY
    this.pulling = false
    if (this.scrollerEl) this.scrollerEl.style.transform = ''
    if (dy > 60) {
      void this.refresh()
    }
  }

  private async refresh(): Promise<void> {
    this.syncing = true
    try {
      await syncListOnNavigate('teams')
      await this.loadTeams()
    } finally {
      this.syncing = false
    }
  }

  private navigateToTeam(teamId: string): void {
    router.navigate(`/teams/${teamId}`)
  }

  override render(): unknown {
    return html`
      <div class="header">
        <button class="back-button" @click=${() => window.history.back()} aria-label="Go back">
          <span class="material-icons" style="font-family: 'Material Symbols Outlined'"
            >arrow_back</span
          >
        </button>
        <h1 class="title">Teams</h1>
      </div>

      ${this.syncing
        ? html`<md-linear-progress class="sync-bar" indeterminate></md-linear-progress>`
        : nothing}

      <div
        class="content"
        @touchstart=${this.handleTouchStart}
        @touchmove=${this.handleTouchMove}
        @touchend=${this.handleTouchEnd}
      >
        ${this.renderNotifications()}

        <div class="add-button">
          ${this.showCreateForm
            ? nothing
            : html`<md-filled-button @click=${() => (this.showCreateForm = true)}
                >Create Team</md-filled-button
              >`}
        </div>

        ${this.showCreateForm ? this.renderCreateForm() : nothing}
        ${this.teams.length > 0 ? this.renderTeamGrid() : this.renderEmptyState()}
      </div>
    `
  }

  private renderNotifications(): unknown {
    if (this.notifications.length === 0) return nothing

    return html`
      <div class="invite-banner">
        <div class="invite-banner-title">Pending Invitations</div>
        ${this.notifications.map(
          n => html`
            <div class="invite-item">
              <div class="invite-info">
                <div class="invite-team-name">${n.entityName ?? n.teamName ?? n.planName}</div>
                <div class="invite-from">
                  Invited by ${n.invitedByName} ${n.type === 'plan-invite' ? '(plan)' : '(team)'}
                </div>
              </div>
              <div class="invite-actions">
                <md-filled-button @click=${() => this.handleAcceptInvite(n)}
                  >Accept</md-filled-button
                >
                <md-outlined-button @click=${() => this.handleDeclineNotification(n)}
                  >Decline</md-outlined-button
                >
              </div>
            </div>
          `
        )}
      </div>
    `
  }

  private renderCreateForm(): unknown {
    const suggestions = this.filteredSuggestions

    return html`
      <div class="create-form">
        <md-outlined-text-field
          label="Team Name"
          .value=${this.createName}
          @input=${(e: InputEvent) => (this.createName = (e.target as HTMLInputElement).value)}
        ></md-outlined-text-field>

        <rich-text-editor
          label="Description"
          minHeight="80px"
          .value=${this.createDescriptionRich}
          @content-changed=${(e: CustomEvent<RichTextContentEvent>): void => {
            this.createDescriptionRich = e.detail.json
            this.createDescription = e.detail.text
          }}
        ></rich-text-editor>

        <div class="invite-section">
          ${this.inviteEmails.length > 0
            ? html`<div class="invite-chips">
                ${this.inviteEmails.map(
                  email => html`
                    <span class="invite-chip">
                      ${email}
                      <button @click=${() => this.removeInviteEmail(email)}>close</button>
                    </span>
                  `
                )}
              </div>`
            : nothing}

          <md-outlined-text-field
            label="Invite members (email)"
            type="email"
            .value=${this.inviteInput}
            @input=${(e: InputEvent) => this.handleInviteInput(e)}
            @keydown=${(e: KeyboardEvent) => this.handleInviteKeydown(e)}
            @blur=${() =>
              setTimeout(() => {
                this.showSuggestions = false
              }, 200)}
            @focus=${() => {
              if (this.inviteInput && suggestions.length > 0) this.showSuggestions = true
            }}
            style="width: 100%;"
          ></md-outlined-text-field>

          ${this.showSuggestions && suggestions.length > 0
            ? html`<div class="suggestions">
                ${suggestions.map(
                  user => html`
                    <div
                      class="suggestion-item"
                      @mousedown=${(e: Event) => {
                        e.preventDefault()
                        this.addInviteEmail(user.email)
                      }}
                    >
                      <span class="suggestion-name">${user.displayName}</span>
                      <span class="suggestion-email">${user.email}</span>
                    </div>
                  `
                )}
              </div>`
            : nothing}
        </div>

        <div class="form-actions">
          <md-text-button
            @click=${() => {
              this.showCreateForm = false
              this.inviteEmails = []
              this.inviteInput = ''
            }}
            >Cancel</md-text-button
          >
          <md-filled-button
            @click=${() => this.handleCreateTeam()}
            ?disabled=${!this.createName.trim() || this.creating}
            >${this.creating ? 'Creating...' : 'Create'}</md-filled-button
          >
        </div>
      </div>
    `
  }

  private renderTeamGrid(): unknown {
    return html`
      <div class="team-grid">
        ${this.teams.map(
          team => html`
            <div class="team-card" @click=${() => this.navigateToTeam(team.id)}>
              <div class="team-name">${team.name}</div>
              <div class="team-description">${team.description}</div>
              <div class="team-meta">
                <span class="role-badge ${team.role}">${team.role}</span>
                <span>${team.memberCount} member${team.memberCount !== 1 ? 's' : ''}</span>
              </div>
            </div>
          `
        )}
      </div>
    `
  }

  private renderEmptyState(): unknown {
    if (this.showCreateForm) return nothing
    return html`
      <div class="empty-state">
        <div class="empty-icon">groups</div>
        <div>No teams yet. Create one to start collaborating.</div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cairn-teams': CairnTeams
  }
}
